import Layout from "../Layout/Layout";
import { useEffect, useState } from "react";
import { getPages } from "./utilities/ApiCalls";
import { Helmet } from "react-helmet-async";
import { setPageTitle } from "../utils/titleUtils";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import { Users, Feather, Flame, Sparkles } from "lucide-react";

export default function WhoWeAre() {
  const location = useLocation();
  const defaultWho = {
    title: "Who We Are",
    content: "StoryTent was founded on the belief that artistic brilliance resides within everyone. We are a passionate team of creators, designers, poets, and storytellers building a home where wordsmiths of all backgrounds find encouragement, recognition, and an audience that truly resonates with their voice."
  };
  const [about, setAbout] = useState(defaultWho);

  useEffect(() => {
    setPageTitle(location.pathname);
    getPages({ 'title': 'who we are' }).then((response) => {
      if (response && response.data && response.data.content) {
        setAbout(response.data);
      }
    }).catch((error) => {
      console.error("Error fetching who-we-are content:", error);
    });
  }, [location]);

  return (
    <Layout>
      <Helmet>
        <title>Who We Are | StoryTent</title>
        <meta name="description" content="Learn more about the creators, team, and community behind StoryTent." />
        <meta property="og:title" content="Who We Are | StoryTent" />
        <meta property="og:description" content="Learn more about the creators, team, and community behind StoryTent." />
        <meta property="og:url" content="https://www.storytent.in/who-we-are" />
        <link rel="canonical" href="https://www.storytent.in/who-we-are" />
      </Helmet>

      <PageWrapper>
        <div className="container">
          <HeroCard>
            <IconBadge>
              <Users size={30} color="#2dd4bf" />
            </IconBadge>
            <MainTitle>{about.title || "Who We Are"}</MainTitle>
            <HeroDescription>{about.content || defaultWho.content}</HeroDescription>
          </HeroCard>

          <ValuesGrid>
            <ValueCard>
              <ValueIconWrap>
                <Feather size={24} color="#38bdf8" />
              </ValueIconWrap>
              <ValueTitle>Wordsmiths & Storytellers</ValueTitle>
              <ValueText>
                From debut poets to seasoned authors, we unite diverse voices sharing compelling narratives in English, Hindi, and regional languages.
              </ValueText>
            </ValueCard>

            <ValueCard>
              <ValueIconWrap>
                <Flame size={24} color="#f43f5e" />
              </ValueIconWrap>
              <ValueTitle>Passion-Driven Culture</ValueTitle>
              <ValueText>
                Every feature we build—from open mic stages to curated readers' feeds—is designed to elevate artistic expression and authentic feedback.
              </ValueText>
            </ValueCard>

            <ValueCard>
              <ValueIconWrap>
                <Sparkles size={24} color="#f59e0b" />
              </ValueIconWrap>
              <ValueTitle>Inclusivity & Community</ValueTitle>
              <ValueText>
                We foster a supportive, welcoming haven free of gatekeeping where every creator is valued and celebrated.
              </ValueText>
            </ValueCard>
          </ValuesGrid>
        </div>
      </PageWrapper>
    </Layout>
  );
}

const PageWrapper = styled.div`
  background: transparent;
  color: var(--color-text, #ffffff);
  padding: 30px 0 60px;
  min-height: 80vh;
`;

const HeroCard = styled.div`
  background: var(--color-surface, #141f1e);
  border: 1px solid var(--color-border, rgba(255, 255, 255, 0.1));
  border-radius: 16px;
  padding: 36px 30px;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.18);
  text-align: center;
  max-width: 920px;
  margin: 0 auto 36px;
`;

const IconBadge = styled.div`
  width: 58px;
  height: 58px;
  border-radius: 50%;
  background: rgba(45, 212, 191, 0.12);
  border: 1px solid rgba(45, 212, 191, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 18px;
`;

const MainTitle = styled.h1`
  color: var(--color-text, #ffffff);
  font-size: 2.2rem;
  font-weight: 700;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    font-size: 1.7rem;
  }
`;

const HeroDescription = styled.p`
  color: var(--color-text-secondary, #cbd5e1);
  font-size: 1.12rem;
  line-height: 1.8;
  max-width: 800px;
  margin: 0 auto;
  text-align: justify;

  @media (max-width: 768px) {
    font-size: 1rem;
    text-align: left;
  }
`;

const ValuesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  max-width: 920px;
  margin: 0 auto;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ValueCard = styled.div`
  background: var(--color-surface, #141f1e);
  border: 1px solid var(--color-border, rgba(255, 255, 255, 0.1));
  border-radius: 14px;
  padding: 24px 20px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  transition: transform 0.2s ease, border-color 0.2s ease;

  &:hover {
    transform: translateY(-3px);
    border-color: rgba(45, 212, 191, 0.3);
  }
`;

const ValueIconWrap = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
`;

const ValueTitle = styled.h2`
  font-size: 1.15rem;
  font-weight: 600;
  color: var(--color-text, #ffffff);
  margin-bottom: 8px;
`;

const ValueText = styled.p`
  font-size: 0.95rem;
  color: var(--color-text-secondary, #94a3b8);
  line-height: 1.6;
  margin: 0;
`;